import {Action, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import {Inject} from 'inversify-props';

import {LogdownLogger} from '@/utils/LogdownLogger';
import {ILogger} from '@/utils/ILogger';

import {LoraForwarder as LoraForwarderModel, LoraForwarder} from '@/model/lora/LoraForwarder';
import {LoraForwarderState} from '@/model/lora/LoraForwarderState';
import {Event} from '@/model/Event';

import {ILoraForwardersService} from '@/services/lora/ILoraForwarders.service';
import {IEventsService} from '@/services/IEvents.service';
import {ISettingsService} from '@/services/settings/ISettings.service';
import {Setting, Theme} from '@/services/settings/Settings.types';

@Module({name: 'LoraForwardersStore', namespaced: true})
export default class LoraForwardersStore extends VuexModule {
  protected logger: ILogger = new LogdownLogger('LoraForwardersStore');

  @Inject(Symbol.for('ILoraForwardersService'))
  protected forwardersService!: ILoraForwardersService;

  @Inject(Symbol.for('IEventsService'))
  protected eventsService!: IEventsService;

  @Inject(Symbol.for('ISettingsService'))
  private settingsService!: ISettingsService;

  private availableForwarders: LoraForwarder[] | null = null;

  private activeForwarder: LoraForwarder | null = null;

  private activeForwarderState: LoraForwarderState = LoraForwarderState.Unknown;
  private stateSubscriptionId: number | null = null;

  private forwardersOrder: Map<string, number> = new Map([
    ['udp-packet-forwarder', 1],
    ['chirpstack-gateway-bridge', 3],
    ['loriot-packet-forwarder', 4],
    ['lora-basic-station', 2],
  ]);

  private forwardersGroups: Map<string, string> = new Map([
    ['lora-basic-station', 'generic'],
    ['udp-packet-forwarder', 'generic'],
    ['loriot-packet-forwarder', 'solution'],
    ['chirpstack-gateway-bridge', 'solution'],
  ]);

  private forwardersLogoSmallLightName: Map<string, string> = new Map([
    ['loriot-packet-forwarder', 'loriot-packet-forwarder-small.png'],
    ['chirpstack-gateway-bridge', 'chirpstack-gateway-bridge-small-light.png'],
  ]);

  private forwardersLogoSmallDarkName: Map<string, string> = new Map([
    ['loriot-packet-forwarder', 'loriot-packet-forwarder-small.png'],
    ['chirpstack-gateway-bridge', 'chirpstack-gateway-bridge-small-dark.png'],
  ]);

  private forwardersLogoLargeLightName: Map<string, string> = new Map([
    ['loriot-packet-forwarder', 'loriot-packet-forwarder-large-light.png'],
    ['chirpstack-gateway-bridge', 'chirpstack-gateway-bridge-large-light.png'],
  ]);

  private forwardersLogoLargeDarkName: Map<string, string> = new Map([
    ['loriot-packet-forwarder', 'loriot-packet-forwarder-large-dark.png'],
    ['chirpstack-gateway-bridge', 'chirpstack-gateway-bridge-large-dark.png'],
  ]);

  /* Available forwarders */
  @Action({commit: 'setAvailableForwarders', rawError: true})
  public async fetchAvailableForwarders() {
    let forwarders = await this.forwardersService.getAvailable();

    forwarders = forwarders.sort((a: LoraForwarderModel, b: LoraForwarderModel) => {
      const aOrder = this.forwardersOrder.get(a.Id) || 1000;
      const bOrder = this.forwardersOrder.get(b.Id) || 1000;
      if (aOrder > bOrder) {
        return 1;
      }
      if (aOrder < bOrder) {
        return -1;
      }
      return 0;
    });
    forwarders.map((f) => {
      f.Group = this.forwardersGroups.get(f.Id) || '';
    });

    return forwarders;
  }

  @Mutation
  public setAvailableForwarders(forwarders: LoraForwarder[]) {
    this.availableForwarders = forwarders;
  }

  get AvailableForwarders(): LoraForwarder[] | null {
    return this.availableForwarders;
  }

  /* Active forwarder */
  @Action({commit: 'setActiveForwarder', rawError: true})
  public async fetchActiveForwarder() {
    const forwarder = await this.forwardersService.getActive();

    if (!forwarder) {
      return null;
    }

    forwarder.Group = this.forwardersGroups.get(forwarder.Id) || '';

    return forwarder;
  }

  @Mutation
  public setActiveForwarder(forwarder: LoraForwarder) {
    if (this.activeForwarder === forwarder) {
      return;
    }

    this.activeForwarder = forwarder;
  }

  @Mutation
  public setActiveForwarderVersion(version: string) {
    if (this.activeForwarder === null) {
      return;
    }

    this.activeForwarder.Version = version;
  }

  get ActiveForwarder(): LoraForwarder | null {
    return this.activeForwarder;
  }


  /* State */
  @Action({commit: 'setActiveForwarderState', rawError: true})
  public async fetchActiveForwarderState() {
    return await this.forwardersService.getActiveForwarderState();
  }

  @Action({commit: 'setStateSubscriptionId', rawError: true})
  public async subscribeActiveForwarderState() {
    return await this.eventsService.subscribe('lora.forwarder.state', async (event: Event) => {
      this.context.commit('setActiveForwarderState', event.Data);
    });
  }

  @Action({rawError: true})
  public async unsubscribeActiveForwarderState() {
    if (this.stateSubscriptionId !== null) {
      await this.eventsService.unsubscribe(this.stateSubscriptionId);
    }
  }

  @Mutation
  public setStateSubscriptionId(id: number) {
    this.stateSubscriptionId = id;
  }

  @Mutation
  public setActiveForwarderState(state: LoraForwarderState) {
    this.activeForwarderState = state;
  }

  get ActiveForwarderState(): LoraForwarderState {
    return this.activeForwarderState;
  }

  get ForwardersLogoSmallName(): Map<string, string> {
    if (this.settingsService.get(Setting.Theme) === Theme.Dark) {
      return this.forwardersLogoSmallDarkName;
    } else {
      return this.forwardersLogoSmallLightName;
    }
  }

  get ForwardersLogoLargeName(): Map<string, string> {
    if (this.settingsService.get(Setting.Theme) === Theme.Dark) {
      return this.forwardersLogoLargeDarkName;
    } else {
      return this.forwardersLogoLargeLightName;
    }
  }
}
